/**
 * Client side of the LV client config — the per-deployment parameters served at
 * GET /LearnerView/api/config (the `learnerView` key of the client DB's
 * Flags.Json). The shell consumes it to decide which surfaces exist for this
 * learner, what each is called and which icon it wears, which colours/fonts are
 * on offer and whether night mode exists, and whether audio playback is on.
 *
 * A null config (unconfigured deployment) means everything-default: all synced
 * surfaces available with their manifest label/icon, default palette + font,
 * night mode allowed, audio enabled.
 */
import { createContext, useContext } from 'react'
import { lighten, darken, decomposeColor } from '@mui/material/styles'
import { pageSurfaceById, type LvSurfaceDef } from './registry'
import { apiFetch } from './auth'

export interface LvSurfaceOverride {
  enabled?: boolean
  label?: string
  icon?: string
  /** External-target override (the classic purchase-redirect made real): the
   *  launcher/Home open this URL in a new tab instead of a surface tab. */
  href?: string
  /** Auto-open at login (nav.<id>.auto): the shell pre-opens a tab per auto
   *  surface at boot — one NAMED TAB PER ITEM for a menuOp surface. Enforced
   *  on LOGIN only; a refreshed layout restores what the learner had. */
  auto?: boolean
  /** Locked tabs (nav.<id>.locked): a tab of this surface can NEVER be
   *  closed — no X, the close guard refuses. */
  locked?: boolean
  /** Pin affordance (nav.<id>.allowPinning) — INVERTED polarity vs auto/
   *  locked: ABSENT = allowed; only explicit false restricts. False = no
   *  pin control on this surface's tabs and stored pins are void. */
  allowPinning?: boolean
  /** Close enforcement (nav.<id>.allowClose) — same inverted polarity:
   *  absent = closable; false bars dismissing a tab that was AUTO-LOADED
   *  at login (learner-opened tabs of the same surface still close). */
  allowClose?: boolean
  /** CUSTOM PAGES ONLY (page-<slug> ids, emitted by the config route): the
   *  shell route — '/page/<slug>'. Static surfaces carry theirs in the
   *  registry's manifest fragments. */
  route?: string
  /** CUSTOM PAGES ONLY: the shell component this dynamic surface maps to —
   *  'page' → the registry's LvPageCanvas synthesis. */
  component?: string
}

/**
 * The deployment's BRAND — the knobs authored in capcom (Learner Views →
 * Branding), stored as brand.* keys in lv_settings and served here by the
 * config route. Everything is optional; absent = the built-in default look.
 * "Not a website" (Douglas 2026-09-20): the client picks from a small set of
 * opinionated knobs — the shell derives everything else (hover/contrast via
 * MUI's palette augmentation, tints, the dark palette) and there is no
 * custom-CSS escape hatch.
 *
 * v2 (2026-10-06, branding expansion — 14 new optional keys; ALL default to
 * the pre-expansion rendering when absent, the D5 parity invariant):
 *
 *   primary     #rrggbb — buttons, active states, links, focus rings
 *   secondary   #rrggbb — second palette colour (owns MUI palette.secondary);
 *                         absent = derived from primary (resolver)
 *   accent      #rrggbb — accent token (badges, highlights, secondary
 *                         buttons); absent = derived from primary (resolver)
 *   logoUrl     https://… or data:image/…;base64 — strip-bar + sign-in gate
 *               logo, and the favicon. Blank = the product name as text.
 *   logoUrlDark dark-mode logo; absent = logoUrl
 *   logoAlt     logo alt text; absent = the product name
 *   radius      sharp (0) | rounded (8, the default) | pill (16)
 *   fontHeading / fontBody — ids into BRAND_FONTS below (the curated list —
 *                         arbitrary families are never loaded). Heading falls
 *                         back to the body font; body to the legacy
 *                         theme.fonts[0] / system stack.
 *   fontSize    small | medium | large — semantic base size (concrete px is
 *                         a Storybook-reviewed theme-builder constant, D6)
 *   fontWeightHeading 400|500|600|700 · fontWeightBody 400|500
 *   <role>Source (link/buttonPrimary/buttonSecondary/active/badge/highlight/
 *               focus) — primary | secondary | accent: which resolved brand
 *               colour drives each UI role
 *   elevation   flat | subtle | elevated — the shadow recipe
 *   nightMode   off (light only) | toggle (learner toggle, light default) |
 *                         dark (learner toggle, dark default). Absent = the
 *                         legacy theme.nightMode flag.
 *
 * v3 (2026-10-09, typography expansion — Isobel):
 *   fonts       [{id, name, provider, family, url?}] — the Font Families
 *                         library: customer-added families (Google Fonts by
 *                         name, or an uploaded custom font file) on top of
 *                         the built-in curated list. Stacks reference library
 *                         entries by id.
 *   fontBodyStack / fontHeadingStack — {custom, mac, pc, category}: the
 *                         4-slot font stack. custom = a library id; mac/pc =
 *                         the most-supported system fonts per platform;
 *                         category = the generic CSS family ending the
 *                         cascade. Composed in order so a Mac takes its
 *                         branch, a PC takes its own, and every browser lands
 *                         on the category. A stack wins over the legacy
 *                         fontBody/fontHeading keys when it resolves.
 *   baseFontSize 14|16|18|20|22|24 — accessible base px (14 floor). Wins over
 *                         the semantic fontSize when both are stored.
 *   headings    {h1..h4: {fontSize?, fontWeight?}} — per-header px size
 *                         (18|20|24|28|32|36|40|48) and weight (400–800).
 *                         Unset levels inherit stock MUI sizing and the
 *                         heading-wide fontWeightHeading; h5/h6 always keep
 *                         the heading-wide defaults.
 */
export type BrandColorSource = 'primary' | 'secondary' | 'accent'

// ── v3 typography shapes (mirrors of the contract shapes in
//    server/lib/brandContract.ts — the src/server boundary duplication is
//    deliberate; keep the two in sync) ───────────────────────────────────────

export type BrandFontProvider = 'google' | 'custom'

/** One entry in the theme's Font Families library (brand.fonts). */
export interface BrandFontLibraryEntry {
  id: string
  name: string
  provider: BrandFontProvider
  /** The CSS font-family name (Google family name, or the @font-face name). */
  family: string
  /** provider=custom only: https URL of the uploaded font file. */
  url?: string
}

/** The 4-slot font stack: library custom → Mac fallback → PC fallback →
 *  generic category. */
export interface BrandFontStack { custom?: string; mac?: string; pc?: string; category?: string }

export type BrandHeadingLevel = 'h1' | 'h2' | 'h3' | 'h4'
export interface BrandHeadingStyle { fontSize?: number; fontWeight?: 400 | 500 | 600 | 700 | 800 }

export interface LvBrand {
  primary?: string
  secondary?: string
  accent?: string
  logoUrl?: string
  logoUrlDark?: string
  logoAlt?: string
  radius?: 'sharp' | 'rounded' | 'pill'
  fontHeading?: string
  fontBody?: string
  fontSize?: 'small' | 'medium' | 'large'
  fontWeightHeading?: 400 | 500 | 600 | 700
  fontWeightBody?: 400 | 500
  fonts?: BrandFontLibraryEntry[]
  fontBodyStack?: BrandFontStack
  fontHeadingStack?: BrandFontStack
  baseFontSize?: 14 | 16 | 18 | 20 | 22 | 24
  headings?: Partial<Record<BrandHeadingLevel, BrandHeadingStyle>>
  nightMode?: 'off' | 'toggle' | 'dark'
  linkSource?: BrandColorSource
  buttonPrimarySource?: BrandColorSource
  buttonSecondarySource?: BrandColorSource
  activeSource?: BrandColorSource
  badgeSource?: BrandColorSource
  highlightSource?: BrandColorSource
  focusSource?: BrandColorSource
  elevation?: 'flat' | 'subtle' | 'elevated'
}

/** The curated font list. Ids are what lv_settings stores; `google` is the
 *  Google Fonts family name loaded on demand (display=swap) — EMPTY for a
 *  system font, which loads nothing; `stack` overrides the composed fallback
 *  stack for those. MIRROR of BRAND_FONT_IDS in capcom
 *  apps/api/server/routes/learnerViews.ts — the write-side validator — keep
 *  the two in sync when a family lands. */
export interface BrandFont { id: string; label: string; google: string; serif?: boolean; stack?: string }
export const BRAND_FONTS: BrandFont[] = [
  { id: 'inter', label: 'Inter', google: 'Inter' },
  { id: 'roboto', label: 'Roboto', google: 'Roboto' },
  { id: 'open-sans', label: 'Open Sans', google: 'Open Sans' },
  { id: 'lato', label: 'Lato', google: 'Lato' },
  { id: 'montserrat', label: 'Montserrat', google: 'Montserrat' },
  { id: 'source-sans-3', label: 'Source Sans 3', google: 'Source Sans 3' },
  { id: 'poppins', label: 'Poppins', google: 'Poppins' },
  { id: 'nunito', label: 'Nunito', google: 'Nunito' },
  { id: 'work-sans', label: 'Work Sans', google: 'Work Sans' },
  { id: 'merriweather', label: 'Merriweather', google: 'Merriweather', serif: true },
  // The classic LMS skin's typeface (thinkingcap.com + Cox Campus branding
  // both run Tahoma) — a system stack, no webfont load.
  { id: 'tahoma', label: 'Tahoma', google: '', stack: 'Tahoma, Verdana, "Segoe UI", sans-serif' },
]
const BRAND_FONT_BY_ID = new Map(BRAND_FONTS.map((f) => [f.id, f]))

// ── v3 platform/category catalogs ────────────────────────────────────────────
// Display catalogs for the stack slots. The contract (server/lib/
// brandContract.ts: BRAND_MAC_FONT_IDS / BRAND_PC_FONT_IDS /
// BRAND_FONT_CATEGORY_IDS) owns the id validation; these own labels + the
// exact CSS family tokens. MIRRORS — lvConfig.brand.test.ts asserts the id
// sets match the contract's exactly (drift = a failing test).

export interface StackFontOption { id: string; label: string; css: string }

/** Mac system font fallbacks — the most-supported macOS families (sans,
 *  then serif, then mono). */
export const MAC_SYSTEM_FONTS: StackFontOption[] = [
  { id: 'helvetica-neue', label: 'Helvetica Neue', css: '"Helvetica Neue"' },
  { id: 'helvetica', label: 'Helvetica', css: 'Helvetica' },
  { id: 'arial', label: 'Arial', css: 'Arial' },
  { id: 'verdana', label: 'Verdana', css: 'Verdana' },
  { id: 'geneva', label: 'Geneva', css: 'Geneva' },
  { id: 'trebuchet-ms', label: 'Trebuchet MS', css: '"Trebuchet MS"' },
  { id: 'lucida-grande', label: 'Lucida Grande', css: '"Lucida Grande"' },
  { id: 'avenir', label: 'Avenir', css: 'Avenir' },
  { id: 'optima', label: 'Optima', css: 'Optima' },
  { id: 'gill-sans', label: 'Gill Sans', css: '"Gill Sans"' },
  { id: 'futura', label: 'Futura', css: 'Futura' },
  { id: 'georgia', label: 'Georgia', css: 'Georgia' },
  { id: 'palatino', label: 'Palatino', css: 'Palatino' },
  { id: 'times-new-roman', label: 'Times New Roman', css: '"Times New Roman"' },
  { id: 'menlo', label: 'Menlo', css: 'Menlo' },
  { id: 'monaco', label: 'Monaco', css: 'Monaco' },
  { id: 'courier-new', label: 'Courier New', css: '"Courier New"' },
]

/** PC system font fallbacks — the most-supported Windows families (the
 *  Vista-and-later ClearType set first, then the core web set, then mono). */
export const PC_SYSTEM_FONTS: StackFontOption[] = [
  { id: 'segoe-ui', label: 'Segoe UI', css: '"Segoe UI"' },
  { id: 'tahoma', label: 'Tahoma', css: 'Tahoma' },
  { id: 'calibri', label: 'Calibri', css: 'Calibri' },
  { id: 'candara', label: 'Candara', css: 'Candara' },
  { id: 'corbel', label: 'Corbel', css: 'Corbel' },
  { id: 'arial', label: 'Arial', css: 'Arial' },
  { id: 'verdana', label: 'Verdana', css: 'Verdana' },
  { id: 'trebuchet-ms', label: 'Trebuchet MS', css: '"Trebuchet MS"' },
  { id: 'franklin-gothic-medium', label: 'Franklin Gothic Medium', css: '"Franklin Gothic Medium"' },
  { id: 'georgia', label: 'Georgia', css: 'Georgia' },
  { id: 'cambria', label: 'Cambria', css: 'Cambria' },
  { id: 'constantia', label: 'Constantia', css: 'Constantia' },
  { id: 'times-new-roman', label: 'Times New Roman', css: '"Times New Roman"' },
  { id: 'consolas', label: 'Consolas', css: 'Consolas' },
  { id: 'courier-new', label: 'Courier New', css: '"Courier New"' },
]

/** Generic CSS font categories — the terminal slot of every stack. The css
 *  is the bare keyword: a quoted generic becomes a NAMED font, never the
 *  generic fallback. */
export const FONT_CATEGORIES: StackFontOption[] = [
  { id: 'sans-serif', label: 'Sans serif', css: 'sans-serif' },
  { id: 'serif', label: 'Serif', css: 'serif' },
  { id: 'monospace', label: 'Monospace', css: 'monospace' },
  { id: 'cursive', label: 'Cursive', css: 'cursive' },
  { id: 'fantasy', label: 'Fantasy', css: 'fantasy' },
  { id: 'system-ui', label: 'System UI', css: 'system-ui' },
]

const MAC_FONT_BY_ID = new Map(MAC_SYSTEM_FONTS.map((f) => [f.id, f]))
const PC_FONT_BY_ID = new Map(PC_SYSTEM_FONTS.map((f) => [f.id, f]))
const FONT_CATEGORY_BY_ID = new Map(FONT_CATEGORIES.map((f) => [f.id, f]))

export interface LvFolderChild {
  /** Always a custom page id (page-<slug>) — folders group pages only. */
  id: string
  label: string
  icon?: string
}

/** One nav folder (nav.folder-<slug>.* + folder.<slug>.def), emitted by the
 *  config route only when the folder is enabled, its def is usable, and ≥1
 *  child resolved. Grouping only — a folder never opens a tab; its children
 *  (page-<slug> ids) do. */
export interface LvFolderEntry {
  /** folder-<slug> — a nav id, never a surface id. */
  id: string
  label: string
  icon?: string
  children: LvFolderChild[]
}

export interface LvClientConfig {
  surfaces?: Record<string, LvSurfaceOverride>
  theme?: { colors?: string[]; fonts?: string[]; nightMode?: boolean }
  brand?: LvBrand
  audio?: { enabled?: boolean; [k: string]: unknown }
  /** Shell chrome toggles resolved from the settings store. */
  shell?: {
    inboxIcon?: boolean
    /** Console-wide forced fullscreen (nav.fullscreen): the chat panel is
     *  hidden completely and the maximize toggle is removed. One flag for the
     *  whole LV — NOT surface-by-surface (Douglas 2026-10-03). */
    fullscreen?: boolean
    /** Tab zero's label/icon substitution (nav.home.*): Cox calls its Home
     *  something of its own and gives it a small client mark. */
    home?: { label?: string; icon?: string }
  }
  /** Which plane answered: the lv_settings store or the flags fallback. */
  settingsSource?: 'lv_settings' | 'flags'
  /** Nav folders (settings store): Explore-menu grouping — a folder never
   *  opens a tab; its children (page-<slug> ids) do. Folder-claimed pages are
   *  NOT in `surfaces` (the config route emits them grouped, not loose).
   *  Additive key: absent on old configs and the flags fallback. */
  folders?: LvFolderEntry[]
  /** The operator's top-level nav order (nav.order): surface ids, page-<slug>
   *  and folder-<slug> ids in menu order. Absent = the shell's default order,
   *  byte-identical to before folders existed. */
  navOrder?: string[]
  /** The deployment's LMS origin (server-emitted): classic handoff URLs
   *  (media details, legacy deep links) build from it. Absent on old servers
   *  — callers fall back to their baked-in default. */
  lmsBaseUrl?: string
  [k: string]: unknown
}

export const DEFAULT_PRIMARY = '#1f3864'
export const DEFAULT_MODE_ALLOWED = true
export const DEFAULT_AUDIO_ENABLED = true

/**
 * ?lv_brand=<base64url-JSON> — the operator preview override from capcom's
 * Branding dialog: merges over the fetched brand for THIS browser only (never
 * persisted anywhere; the values run through the same resolver sanitizers as
 * stored rows). Presentation-only — the worst a forged param does is restyle
 * the forger's own browser.
 */
function brandPreviewOverride(): Partial<LvBrand> | null {
  try {
    const p = new URLSearchParams(window.location.search).get('lv_brand')
    if (!p) return null
    let b64 = p.replace(/-/g, '+').replace(/_/g, '/')
    b64 += '='.repeat((4 - (b64.length % 4)) % 4)
    const raw = JSON.parse(decodeURIComponent(escape(atob(b64)))) as unknown
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null
    return raw as Partial<LvBrand>
  } catch {
    return null
  }
}

/** The config fetch carries the session when one exists (apiFetch adds the
 *  bearer): branch-scoped settings resolve per learner. Pre-login (LoginGate)
 *  it stays anonymous = client-wide rows only. */
export async function fetchLvConfig(): Promise<LvClientConfig | null> {
  try {
    const r = await apiFetch('/LearnerView/api/config')
    if (!r.ok) return null
    const body = (await r.json()) as { config?: LvClientConfig | null }
    const cfg = body.config ?? null
    const preview = brandPreviewOverride()
    const merged = preview ? { ...(cfg ?? {}), brand: { ...(cfg?.brand ?? {}), ...preview } } : cfg
    lastConfig = merged
    return merged
  } catch {
    return null
  }
}

/** The most recently fetched config (module-level — blocks/widgets that are
 *  not in the shell's prop chain read deployment-wide facts like lmsBaseUrl
 *  here). Undefined until the first fetch lands; the shell always fetches
 *  before any surface mounts. */
let lastConfig: LvClientConfig | null | undefined
export function currentLvConfig(): LvClientConfig | null | undefined {
  return lastConfig
}

/** The surfaces this deployment offers, with per-client label/icon
 *  substitutions applied. A `surfaces` map in config is an ALLOWLIST (unlisted
 *  or enabled:false → hidden); no map → everything the base image carries.
 *  CUSTOM PAGES: page-<slug> ids the config emits with component 'page' are
 *  not synced surfaces — the registry synthesizes their defs (component
 *  LvPageCanvas) and the emitted label/icon/route + tab-policy flags land
 *  exactly like a static surface's overrides. Pages never take href.
 *  NAV ORDER: when config.navOrder resolved (the operator's top-level order),
 *  the openable entries it names — known AND enabled, folder- ids skipped:
 *  folders are not openable, the masthead consumes them — come first in
 *  listed order; the remainder follows in the default order (alphabetical
 *  statics, then loose pages in server-emission order). No navOrder ⇒
 *  byte-identical behavior to before folders existed. */
export function resolveSurfaces(all: LvSurfaceDef[], cfg: LvClientConfig | null): LvSurfaceDef[] {
  const map = cfg?.surfaces
  const resolved: LvSurfaceDef[] = !map ? all : all
    .filter((s) => map[s.id]?.enabled === true)
    .map((s) => ({
      ...s,
      label: map[s.id]?.label ?? s.label,
      icon: map[s.id]?.icon ?? s.icon,
      href: map[s.id]?.href ?? undefined,
      auto: map[s.id]?.auto === true,
      locked: map[s.id]?.locked === true,
      // Inverted polarity (absent = allowed): only an explicit false restricts.
      allowClose: map[s.id]?.allowClose === false ? false : undefined,
      allowPinning: map[s.id]?.allowPinning === false ? false : undefined,
    }))
  if (map) for (const [id, o] of Object.entries(map)) {
    if (o?.enabled !== true || o.component !== 'page') continue
    const def = pageSurfaceById(id)
    if (!def) continue
    resolved.push({
      ...def,
      label: o.label ?? def.label,
      icon: o.icon ?? def.icon,
      route: o.route ?? def.route,
      auto: o.auto === true,
      locked: o.locked === true,
      // Inverted polarity (absent = allowed), same rule as surfaces.
      allowClose: o.allowClose === false ? false : undefined,
      allowPinning: o.allowPinning === false ? false : undefined,
    })
  }
  const order = cfg?.navOrder
  if (!Array.isArray(order) || order.length === 0) return resolved
  const byId = new Map(resolved.map((s) => [s.id, s]))
  const listed = new Set<string>()
  const out: LvSurfaceDef[] = []
  for (const id of order) {
    if (typeof id !== 'string' || id.startsWith('folder-')) continue
    const s = byId.get(id) // known openable + enabled (disabled/unknown skip)
    if (!s || listed.has(id)) continue
    listed.add(id)
    out.push(s)
  }
  for (const s of resolved) if (!listed.has(s.id)) out.push(s)
  return out
}

// ── Brand resolution ─────────────────────────────────────────────────────────
// brand.* (lv_settings, capcom-authored) wins over the legacy Flags.Json theme
// keys; every stored value is sanitized here so a bad row degrades to the
// default look instead of breaking the theme.

const BRAND_HEX_RE = /^#[0-9a-f]{6}$/i
const BRAND_RADII = new Set(['sharp', 'rounded', 'pill'])
const BRAND_NIGHT_MODES = new Set(['off', 'toggle', 'dark'])
const BRAND_FONT_SIZES = new Set(['small', 'medium', 'large'])
const BRAND_COLOR_SOURCES = new Set(['primary', 'secondary', 'accent'])
const BRAND_ELEVATIONS = new Set(['flat', 'subtle', 'elevated'])
const BRAND_HEADING_WEIGHTS = new Set([400, 500, 600, 700])
const BRAND_BODY_WEIGHTS = new Set([400, 500])
const SANS_STACK = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'

// v3 validator vocabularies (mirror server/lib/brandContract.ts)
const BRAND_BASE_FONT_SIZES = new Set([14, 16, 18, 20, 22, 24])
const BRAND_HEADING_FONT_SIZES = new Set([18, 20, 24, 28, 32, 36, 40, 48])
const BRAND_HEADING_LEVEL_WEIGHTS = new Set([400, 500, 600, 700, 800])
const BRAND_FONT_PROVIDERS = new Set(['google', 'custom'])
const BRAND_FONT_ID_RE = /^[a-z0-9-]{1,40}$/
const BRAND_FONT_FAMILY_RE = /^[A-Za-z0-9][A-Za-z0-9\s.'-]{0,59}$/
const BRAND_FONT_URL_RE = /^https:\/\/[^\s'"()\\]{1,500}$/i
const BRAND_FONT_LIBRARY_CAP = 20

function asBrandHex(v: unknown): string | undefined {
  return typeof v === 'string' && BRAND_HEX_RE.test(v.trim()) ? v.trim().toLowerCase() : undefined
}
function asBrandLogo(v: unknown): string | undefined {
  if (typeof v !== 'string') return undefined
  const s = v.trim()
  if (/^https:\/\//i.test(s) && s.length <= 500) return s
  if (/^data:image\/(png|gif|jpe?g|svg\+xml|webp);base64,[A-Za-z0-9+/=]+$/i.test(s) && s.length <= 300_000) return s
  return undefined
}

/** The resolved (sanitized) brand. Cheap to recompute; memoize at the call
 *  site against the config object when it sits in a render loop. */
export function resolveBrand(cfg: LvClientConfig | null): LvBrand {
  const b = cfg?.brand
  if (!b || typeof b !== 'object') return {}
  const out: LvBrand = {}
  const primary = asBrandHex(b.primary); if (primary) out.primary = primary
  const secondary = asBrandHex(b.secondary); if (secondary) out.secondary = secondary
  const accent = asBrandHex(b.accent); if (accent) out.accent = accent
  const logoUrl = asBrandLogo(b.logoUrl); if (logoUrl) out.logoUrl = logoUrl
  const logoUrlDark = asBrandLogo(b.logoUrlDark); if (logoUrlDark) out.logoUrlDark = logoUrlDark
  if (typeof b.logoAlt === 'string') {
    // eslint-disable-next-line no-control-regex
    const alt = b.logoAlt.replace(/[\x00-\x1f\x7f]/g, '').trim()
    if (alt && alt.length <= 120) out.logoAlt = alt
  }
  if (typeof b.radius === 'string' && BRAND_RADII.has(b.radius)) out.radius = b.radius as LvBrand['radius']
  if (typeof b.nightMode === 'string' && BRAND_NIGHT_MODES.has(b.nightMode)) out.nightMode = b.nightMode as LvBrand['nightMode']
  if (typeof b.fontHeading === 'string' && BRAND_FONT_BY_ID.has(b.fontHeading)) out.fontHeading = b.fontHeading
  if (typeof b.fontBody === 'string' && BRAND_FONT_BY_ID.has(b.fontBody)) out.fontBody = b.fontBody
  if (typeof b.fontSize === 'string' && BRAND_FONT_SIZES.has(b.fontSize)) out.fontSize = b.fontSize as LvBrand['fontSize']
  const wh = asWeight(b.fontWeightHeading, BRAND_HEADING_WEIGHTS); if (wh) out.fontWeightHeading = wh as LvBrand['fontWeightHeading']
  const wb = asWeight(b.fontWeightBody, BRAND_BODY_WEIGHTS); if (wb) out.fontWeightBody = wb as LvBrand['fontWeightBody']
  // v3 typography expansion
  const fonts = asBrandFonts(b.fonts); if (fonts) out.fonts = fonts
  const fontBodyStack = asFontStack(b.fontBodyStack); if (fontBodyStack) out.fontBodyStack = fontBodyStack
  const fontHeadingStack = asFontStack(b.fontHeadingStack); if (fontHeadingStack) out.fontHeadingStack = fontHeadingStack
  const bfs = asWeight(b.baseFontSize, BRAND_BASE_FONT_SIZES); if (bfs) out.baseFontSize = bfs as LvBrand['baseFontSize']
  const headings = asBrandHeadings(b.headings); if (headings) out.headings = headings
  const sourceKeys = ['linkSource', 'buttonPrimarySource', 'buttonSecondarySource', 'activeSource', 'badgeSource', 'highlightSource', 'focusSource'] as const
  for (const k of sourceKeys) {
    const v = b[k]
    if (typeof v === 'string' && BRAND_COLOR_SOURCES.has(v)) out[k] = v as BrandColorSource
  }
  if (typeof b.elevation === 'string' && BRAND_ELEVATIONS.has(b.elevation)) out.elevation = b.elevation as LvBrand['elevation']
  return out
}

/** Weights arrive as jsonb numbers; tolerate digit-strings from hand edits. */
function asWeight(v: unknown, allowed: Set<number>): number | undefined {
  const n = typeof v === 'number' ? v : typeof v === 'string' && /^\d+$/.test(v.trim()) ? Number(v.trim()) : NaN
  return allowed.has(n) ? n : undefined
}

// ── v3 sanitizers (mirror server/lib/brandContract.ts) ───────────────────────

/** The Font Families library: entries kept/dropped individually; first id
 *  wins on duplicates; undefined when nothing valid survives. */
function asBrandFonts(v: unknown): BrandFontLibraryEntry[] | undefined {
  if (!Array.isArray(v)) return undefined
  const seen = new Set<string>()
  const out: BrandFontLibraryEntry[] = []
  for (const raw of v.slice(0, BRAND_FONT_LIBRARY_CAP)) {
    if (!raw || typeof raw !== 'object') continue
    const e = raw as Record<string, unknown>
    const id = typeof e.id === 'string' && BRAND_FONT_ID_RE.test(e.id) ? e.id : undefined
    // eslint-disable-next-line no-control-regex
    const name = typeof e.name === 'string' ? e.name.replace(/[\x00-\x1f\x7f]/g, '').trim() : ''
    const provider = typeof e.provider === 'string' && BRAND_FONT_PROVIDERS.has(e.provider) ? e.provider as BrandFontProvider : undefined
    const family = typeof e.family === 'string' && BRAND_FONT_FAMILY_RE.test(e.family.trim()) ? e.family.trim() : undefined
    const url = typeof e.url === 'string' && BRAND_FONT_URL_RE.test(e.url.trim()) ? e.url.trim() : undefined
    if (!id || !name || name.length > 60 || !provider || !family || seen.has(id)) continue
    if (provider === 'custom' && !url) continue
    out.push(url ? { id, name, provider, family, url } : { id, name, provider, family })
    seen.add(id)
  }
  return out.length ? out : undefined
}

function asFontStack(v: unknown): BrandFontStack | undefined {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return undefined
  const o = v as Record<string, unknown>
  const stack: BrandFontStack = {}
  if (typeof o.custom === 'string' && BRAND_FONT_ID_RE.test(o.custom)) stack.custom = o.custom
  if (typeof o.mac === 'string' && MAC_FONT_BY_ID.has(o.mac)) stack.mac = o.mac
  if (typeof o.pc === 'string' && PC_FONT_BY_ID.has(o.pc)) stack.pc = o.pc
  if (typeof o.category === 'string' && FONT_CATEGORY_BY_ID.has(o.category)) stack.category = o.category
  return Object.keys(stack).length ? stack : undefined
}

function asBrandHeadings(v: unknown): LvBrand['headings'] {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return undefined
  const out: NonNullable<LvBrand['headings']> = {}
  for (const level of ['h1', 'h2', 'h3', 'h4'] as const) {
    const raw = (v as Record<string, unknown>)[level]
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) continue
    const o = raw as Record<string, unknown>
    const style: BrandHeadingStyle = {}
    const size = asWeight(o.fontSize, BRAND_HEADING_FONT_SIZES); if (size) style.fontSize = size
    const weight = asWeight(o.fontWeight, BRAND_HEADING_LEVEL_WEIGHTS); if (weight) style.fontWeight = weight as BrandHeadingStyle['fontWeight']
    if (Object.keys(style).length) out[level] = style
  }
  return Object.keys(out).length ? out : undefined
}

function brandFontStack(f: BrandFont): string {
  if (f.stack) return f.stack
  return f.serif ? `"${f.google}", Georgia, 'Times New Roman', serif` : `"${f.google}", ${SANS_STACK}`
}

/** Quote a library family for the cascade (families with spaces need it;
 *  single-word families take the quotes harmlessly). Generic categories are
 *  NEVER quoted — a quoted generic becomes a named font. */
function quoteFamily(family: string): string {
  return `"${family}"`
}

/** The sanitized Font Families library (brand.fonts) — customer additions
 *  whose ids don't shadow a built-in curated family (built-ins are
 *  immutable; shadowing entries drop here, matching the contract note). */
export function brandFonts(cfg: LvClientConfig | null): BrandFontLibraryEntry[] {
  return (resolveBrand(cfg).fonts ?? []).filter((f) => !BRAND_FONT_BY_ID.has(f.id))
}

/** Look up a stack's custom slot in the merged library: the built-in curated
 *  list first, then the customer's Font Families entries. Returns the CSS
 *  family name to compose (NOT a full curated stack — the 4 slots own the
 *  fallbacks in the v3 model). */
function libraryFamily(cfg: LvClientConfig | null, id: string): string | undefined {
  const builtin = BRAND_FONT_BY_ID.get(id)
  if (builtin) return builtin.google || builtin.label
  return brandFonts(cfg).find((f) => f.id === id)?.family
}

/** Compose a 4-slot stack into a CSS font-family value: the custom library
 *  family, then the Mac system fallback, then the PC system fallback, then
 *  the generic category (default sans-serif when other slots resolve but no
 *  category was picked). A stack whose slots all fail to resolve (unknown
 *  custom id, nothing else set) is treated as UNSET so the legacy font
 *  chain still answers. */
function composeStack(cfg: LvClientConfig | null, stack: BrandFontStack): string | undefined {
  const parts: string[] = []
  if (stack.custom) {
    const family = libraryFamily(cfg, stack.custom)
    if (family) parts.push(quoteFamily(family))
  }
  if (stack.mac) parts.push(MAC_FONT_BY_ID.get(stack.mac)!.css)
  if (stack.pc) parts.push(PC_FONT_BY_ID.get(stack.pc)!.css)
  if (!parts.length && !stack.category) return undefined
  parts.push(stack.category ? FONT_CATEGORY_BY_ID.get(stack.category)!.css : 'sans-serif')
  return parts.join(', ')
}

/** The body/base font stack: v3 fontBodyStack → brand body font → legacy
 *  theme.fonts[0] → undefined (MUI's default stack). */
export function fontFamily(cfg: LvClientConfig | null): string | undefined {
  const b = resolveBrand(cfg)
  if (b.fontBodyStack) {
    const stack = composeStack(cfg, b.fontBodyStack)
    if (stack) return stack
  }
  if (b.fontBody) return brandFontStack(BRAND_FONT_BY_ID.get(b.fontBody)!)
  const f = cfg?.theme?.fonts?.[0]
  return f ? `"${f}", ${SANS_STACK}` : undefined
}

/** The display/heading stack — only when a heading font was explicitly picked
 *  (absent = headings inherit the body font, the typographically safe
 *  default). v3 fontHeadingStack wins over the legacy fontHeading id. */
export function headingFontFamily(cfg: LvClientConfig | null): string | undefined {
  const b = resolveBrand(cfg)
  if (b.fontHeadingStack) {
    const stack = composeStack(cfg, b.fontHeadingStack)
    if (stack) return stack
  }
  return b.fontHeading ? brandFontStack(BRAND_FONT_BY_ID.get(b.fontHeading)!) : undefined
}

/** A webfont the page must load: a Google Fonts family (one combined
 *  stylesheet for all of them) or an uploaded custom file (one @font-face
 *  rule each). */
export type BrandWebfont =
  | { kind: 'google'; family: string }
  | { kind: 'custom'; family: string; url: string }

/** The webfonts the page must load for the resolved brand (deduped by
 *  family). Sources: the legacy curated picks (fontHeading/fontBody) and
 *  the custom slots of both v3 stacks. Google entries load by family name;
 *  custom entries by their uploaded file; system families load nothing —
 *  never an arbitrary family. */
export function brandWebfonts(cfg: LvClientConfig | null): BrandWebfont[] {
  const b = resolveBrand(cfg)
  const out: BrandWebfont[] = []
  const seen = new Set<string>()
  const pushGoogle = (family: string) => {
    const key = `google:${family.toLowerCase()}`
    if (!seen.has(key)) { seen.add(key); out.push({ kind: 'google', family }) }
  }
  // Legacy curated picks
  for (const id of [b.fontHeading, b.fontBody]) {
    const f = id ? BRAND_FONT_BY_ID.get(id) : undefined
    if (f?.google) pushGoogle(f.google)
  }
  // v3 stack custom slots
  for (const stack of [b.fontHeadingStack, b.fontBodyStack]) {
    const id = stack?.custom
    if (!id) continue
    const builtin = BRAND_FONT_BY_ID.get(id)
    if (builtin) { if (builtin.google) pushGoogle(builtin.google); continue }
    const entry = brandFonts(cfg).find((f) => f.id === id)
    if (!entry) continue
    if (entry.provider === 'google') pushGoogle(entry.family)
    else if (entry.url) {
      const key = `custom:${entry.family.toLowerCase()}`
      if (!seen.has(key)) { seen.add(key); out.push({ kind: 'custom', family: entry.family, url: entry.url }) }
    }
  }
  return out
}

/** v3: every font weight the resolved brand can render — the stock UI set
 *  (400 body / 500 medium UI / 600 the ratified heading default / 700 bold)
 *  plus the stored body weight, the heading-wide weight and every per-header
 *  override (brand.headings permits 800). The webfont loader requests exactly
 *  this set from Google so a configured weight never falls back to a
 *  synthesized faux-bold. Sorted ascending, unique. */
export function brandFontWeightSet(cfg: LvClientConfig | null): number[] {
  const weights = new Set<number>([400, 500, 600, 700])
  weights.add(brandFontWeightBody(cfg))
  weights.add(brandFontWeightHeading(cfg))
  for (const style of Object.values(brandHeadings(cfg) ?? {})) {
    if (style?.fontWeight) weights.add(style.fontWeight)
  }
  return [...weights].sort((a, b) => a - b)
}

export function brandAccent(cfg: LvClientConfig | null): string | undefined {
  return resolveBrand(cfg).accent
}
export function brandLogoUrl(cfg: LvClientConfig | null): string | undefined {
  return resolveBrand(cfg).logoUrl
}

// ── v2 resolvers (branding expansion 2026-10-06) ─────────────────────────────
// Defaults + derivations for the 14 new keys. The parity invariant: with all
// 14 absent these return values that reproduce the pre-expansion theme.
// buildLvTheme is switched onto these in the theme-builder step (Step 3);
// until then nothing here is on the render path.

/**
 * Derivation defaults (ratified — Q1 ruling 2026-10-14, Isobel; the customer
 * contract is the SEMANTIC key and these recipes are the documented
 * defaults): secondary is primary's deeper sibling, accent its lighter one,
 * using MUI's own augmentColor recipe (tonalOffset 0.2: light =
 * lighten(main, 0.2), dark = darken(main, 0.3) — createPalette.js;
 * augmentColor itself isn't exported in MUI 6.5, so the recipe is replicated
 * on the public lighten/darken). MUI's manipulator answers rgb() strings;
 * the contract is #rrggbb everywhere, so the result is normalized back to
 * hex. Deterministic pure functions of the resolved primary.
 */
/** Normalize any CSS colour MUI's manipulator hands back (rgb() strings) to
 *  the contract's #rrggbb shape. */
export function toHex(color: string): string {
  if (/^#[0-9a-f]{6}$/i.test(color)) return color.toLowerCase()
  const [r, g, b] = decomposeColor(color).values
  const h = (n: number) => Math.round(n).toString(16).padStart(2, '0')
  return `#${h(r)}${h(g)}${h(b)}`
}
export function deriveSecondaryFromPrimary(primary: string): string {
  return toHex(darken(primary, 0.3))
}
export function deriveAccentFromPrimary(primary: string): string {
  return toHex(lighten(primary, 0.2))
}

/** Resolved secondary: brand.secondary → derive from the resolved primary. */
export function brandSecondary(cfg: LvClientConfig | null): string {
  return resolveBrand(cfg).secondary ?? deriveSecondaryFromPrimary(primaryColor(cfg))
}
/** Resolved accent: brand.accent → derive from the resolved primary.
 *  (Distinct from brandAccent(), which returns only the STORED accent — the
 *  pre-expansion behaviour the theme builder still relies on until Step 3.) */
export function brandAccentResolved(cfg: LvClientConfig | null): string {
  return resolveBrand(cfg).accent ?? deriveAccentFromPrimary(primaryColor(cfg))
}

export function brandFontSize(cfg: LvClientConfig | null): 'small' | 'medium' | 'large' {
  return resolveBrand(cfg).fontSize ?? 'medium'
}
/** v3: the stored accessible base px (brand.baseFontSize), or undefined when
 *  only the semantic fontSize governs — the theme builder owns the semantic
 *  → px mapping, so this resolver never invents a px. */
export function brandBaseFontSize(cfg: LvClientConfig | null): LvBrand['baseFontSize'] {
  return resolveBrand(cfg).baseFontSize
}
/** v3: per-header styles (brand.headings); unset levels simply absent. */
export function brandHeadings(cfg: LvClientConfig | null): LvBrand['headings'] {
  return resolveBrand(cfg).headings
}
export function brandFontWeightHeading(cfg: LvClientConfig | null): 400 | 500 | 600 | 700 {
  return resolveBrand(cfg).fontWeightHeading ?? 600
}
export function brandFontWeightBody(cfg: LvClientConfig | null): 400 | 500 {
  return resolveBrand(cfg).fontWeightBody ?? 400
}
export function brandElevation(cfg: LvClientConfig | null): 'flat' | 'subtle' | 'elevated' {
  return resolveBrand(cfg).elevation ?? 'subtle'
}

/** UI-role mapping (v2): each role names which resolved brand colour drives
 *  it. Defaults reproduce the pre-expansion rendering. */
export function brandLinkSource(cfg: LvClientConfig | null): BrandColorSource {
  return resolveBrand(cfg).linkSource ?? 'primary'
}
export function brandButtonPrimarySource(cfg: LvClientConfig | null): BrandColorSource {
  return resolveBrand(cfg).buttonPrimarySource ?? 'primary'
}
export function brandButtonSecondarySource(cfg: LvClientConfig | null): BrandColorSource {
  return resolveBrand(cfg).buttonSecondarySource ?? 'accent'
}
export function brandActiveSource(cfg: LvClientConfig | null): BrandColorSource {
  return resolveBrand(cfg).activeSource ?? 'primary'
}
export function brandBadgeSource(cfg: LvClientConfig | null): BrandColorSource {
  return resolveBrand(cfg).badgeSource ?? 'accent'
}
export function brandHighlightSource(cfg: LvClientConfig | null): BrandColorSource {
  return resolveBrand(cfg).highlightSource ?? 'accent'
}
export function brandFocusSource(cfg: LvClientConfig | null): BrandColorSource {
  return resolveBrand(cfg).focusSource ?? 'primary'
}

/** Maps a role source to the resolved hex (primary → legacy fallback chain,
 *  secondary/accent → their resolved-or-derived values). */
export function resolveSourceColor(cfg: LvClientConfig | null, source: BrandColorSource): string {
  return source === 'primary' ? primaryColor(cfg) : source === 'secondary' ? brandSecondary(cfg) : brandAccentResolved(cfg)
}

/** Dark-mode logo: brand.logoUrlDark → the main logo. */
export function brandLogoUrlDark(cfg: LvClientConfig | null): string | undefined {
  const b = resolveBrand(cfg)
  return b.logoUrlDark ?? b.logoUrl
}
/** Logo alt text: brand.logoAlt → the product name (D1). */
export function brandLogoAlt(cfg: LvClientConfig | null): string {
  return resolveBrand(cfg).logoAlt ?? productName(cfg)
}

/** The installation's display name for learner-facing chrome (mail cards,
 *  gate titles): the learnerView config's productName, then clientName, else
 *  the generic product label. Same fallback chain as LoginGate. */
export function productName(cfg: LvClientConfig | null): string {
  return (typeof cfg?.productName === 'string' && cfg.productName)
    || (typeof cfg?.clientName === 'string' && cfg.clientName)
    || 'Learner View'
}
/** Corner style in px; undefined = the shell's built-in default (8). */
export function brandRadiusPx(cfg: LvClientConfig | null): number | undefined {
  const r = resolveBrand(cfg).radius
  return r === 'sharp' ? 0 : r === 'pill' ? 16 : r === 'rounded' ? 8 : undefined
}
export function brandNightMode(cfg: LvClientConfig | null): LvBrand['nightMode'] | undefined {
  return resolveBrand(cfg).nightMode
}

export function primaryColor(cfg: LvClientConfig | null): string {
  return resolveBrand(cfg).primary ?? cfg?.theme?.colors?.[0] ?? DEFAULT_PRIMARY
}
export function nightModeAllowed(cfg: LvClientConfig | null): boolean {
  const nm = brandNightMode(cfg)
  if (nm) return nm !== 'off'
  return cfg?.theme?.nightMode ?? DEFAULT_MODE_ALLOWED
}
export function audioEnabled(cfg: LvClientConfig | null): boolean {
  return cfg?.audio?.enabled ?? DEFAULT_AUDIO_ENABLED
}

/** Masthead inbox icon + unread badge (classic myInboxIcon): shown only when
 *  the settings store says so — hidden by default, exactly like classic. */
export function inboxIconEnabled(cfg: LvClientConfig | null): boolean {
  return cfg?.shell?.inboxIcon === true
}

/** The Home tab's label/icon (nav.home.*), with the shell defaults. */
export function homeTabLabel(cfg: LvClientConfig | null): string {
  const l = cfg?.shell?.home?.label
  return typeof l === 'string' && l.trim() ? l.trim() : 'Home'
}
export function homeTabIcon(cfg: LvClientConfig | null): string {
  const i = cfg?.shell?.home?.icon
  return typeof i === 'string' && i.trim() ? i.trim() : 'Home'
}

/** What surfaces read: the resolved config plus the conveniences every surface
 *  is expected to honour (today: audio). */
export interface LvConfigContextValue {
  config: LvClientConfig | null
  audio: { enabled: boolean }
  nightModeAllowed: boolean
}
export const LvConfigContext = createContext<LvConfigContextValue>({
  config: null,
  audio: { enabled: DEFAULT_AUDIO_ENABLED },
  nightModeAllowed: DEFAULT_MODE_ALLOWED,
})
export function useLvConfig(): LvConfigContextValue {
  return useContext(LvConfigContext)
}
